import { useState, forwardRef } from "react";
import {
  RichTreeViewPro,
  TreeItem,
  useTreeViewApiRef,
} from "@mui/x-tree-view-pro";
import { TreeItemLabel } from "@mui/x-tree-view-pro";
import GroupIcon from "@mui/icons-material/Group";
import WorkIcon from "@mui/icons-material/Work";
import PersonIcon from "@mui/icons-material/Person";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem, Stack, Avatar } from "@mui/material";

import { apiUrls } from "../../actprep-ui/utils/urls";
import { capitalize, fullName } from "../../actprep-ui/utils/formatting";
import useLazyTree from "../../actprep-ui/hooks/ui/useLazyTree";

export default function TeamTree({ onItemSelect }) {
  const treeApiRef = useTreeViewApiRef();

  const CustomTreeItem = forwardRef((props, ref) => {
    const item = treeApiRef.current.getItem(props.itemId);
    const [menuPos, setMenuPos] = useState(null);

    const handleMenuOpen = (e) => {
      e.stopPropagation();
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    };
    const handleMenuClose = (e) => {
      e.stopPropagation();
      setMenuPos(null);
    };

    let icon;
    switch (item.type) {
      case "team":
        icon = <GroupIcon fontSize="small" />;
        break;
      case "role":
        icon = <WorkIcon fontSize="small" />;
        break;
      case "admin_profile":
        icon = (
          <Avatar
            src={item.data?.user_profile?.profile_pic}
            sx={{ width: 24, height: 24 }}
          />
        );
        break;
      default:
        icon = null;
    }

    return (
      <TreeItem
        {...props}
        ref={ref}
        slots={{
          label: ({ children, ...labelProps }) => (
            <TreeItemLabel {...labelProps}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {icon}
                <span>{children}</span>

                <IconButton
                  size="small"
                  onClick={handleMenuOpen}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>

                <Menu
                  open={Boolean(menuPos)}
                  anchorReference="anchorPosition"
                  anchorPosition={menuPos}
                  onClose={handleMenuClose}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MenuItem onClick={() => onItemSelect(item)}>
                    Edit {item.type.split("_").map(capitalize).join(" ")}
                  </MenuItem>
                </Menu>
              </Stack>
            </TreeItemLabel>
          ),
        }}
      />
    );
  });

  const typeHierarchyConfig = [
    {
      type: "team",
      baseUrl: apiUrls.admin.teams(),
      getParams: () => ({ values: ["title", "uuid", "role_count"] }),
      getLabel: ({ title }) => title,
      getChildrenCount: ({ role_count }) => role_count,
      icon: <GroupIcon fontSize="small" />,
    },
    {
      type: "role",
      baseUrl: apiUrls.admin.roles(),
      getParams: (parent) => ({
        search_params: { team_id: parent.id },
        values: ["title", "uuid", "admin_profile_count"],
      }),
      getLabel: ({ title }) => title,
      getChildrenCount: ({ admin_profile_count }) => admin_profile_count,
      slots: { icon: <WorkIcon fontSize="small" /> },
    },
    {
      type: "admin_profile",
      baseUrl: apiUrls.admin.adminProfiles(),
      getParams: (parent) => ({
        search_params: { roles__uuid: parent.id },
        values: ["user", "uuid", "user_profile"],
        keep_related: true,
      }),
      getLabel: ({ user }) => fullName(user),
      getChildrenCount: () => 0,
      slots: { icon: <PersonIcon fontSize="small" /> },
    },
  ];

  const { getTreeItems, getChildrenCount } = useLazyTree({
    config: typeHierarchyConfig,
    treeApiRef,
  });

  return (
    <Stack spacing={1}>
      <RichTreeViewPro
        apiRef={treeApiRef}
        items={[]}
        dataSource={{ getChildrenCount, getTreeItems }}
        slots={{ item: CustomTreeItem }}
      />
    </Stack>
  );
}
